import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  ChartNoAxesCombined,
  Clock3,
  Eye,
  LockKeyhole,
  TrendingUp,
} from "lucide-react";

const benefits = [
  {
    icon: Clock3,
    title: "Real-time revenue tracking across Client projects",
  },
  {
    icon: BriefcaseBusiness,
    title: "Project-wise and Team-wise revenue visibility",
  },
  {
    icon: BarChart3,
    title: "Interactive dashboards and performance analytics",
  },
  {
    icon: TrendingUp,
    title: "Trend analysis and historical reporting",
  },
  {
    icon: ChartNoAxesCombined,
    title: "Improved forecasting and operational decision-making",
  },
  {
    icon: LockKeyhole,
    title: "Secure, role-based access to financial information",
  },
];

function RevenuePreview() {
  return (
    <div className="crt-preview" aria-label="Illustrative revenue dashboard">
      <div className="crt-preview-top">
        <div className="crt-preview-title">
          <span className="crt-preview-mark"><Eye size={16} /></span>
          <span>
            <strong>Revenue overview</strong>
            <small>Client performance</small>
          </span>
        </div>
        <span className="crt-period">All projects <span aria-hidden="true">⌄</span></span>
      </div>
      <div className="crt-preview-body">
        <div className="crt-chart-heading">
          <span>Revenue trend</span>
          <span className="crt-chart-legend"><i /> Actuals <i className="crt-legend-target" /> Targets</span>
        </div>
        <div className="crt-chart" aria-hidden="true">
          <div className="crt-chart-grid">
            <span />
            <span />
            <span />
          </div>
          <svg viewBox="0 0 520 170" preserveAspectRatio="none">
            <path className="crt-chart-area" d="M0 133 C38 126 44 94 87 103 S143 115 173 83 S228 102 260 66 S315 88 346 56 S405 71 432 35 S482 48 520 17 V170 H0 Z" />
            <path className="crt-chart-target-line" d="M0 112 C66 105 90 97 142 93 S207 82 260 77 S347 62 403 59 S475 45 520 42" />
            <path className="crt-chart-line" d="M0 133 C38 126 44 94 87 103 S143 115 173 83 S228 102 260 66 S315 88 346 56 S405 71 432 35 S482 48 520 17" />
            <circle cx="520" cy="17" r="5" />
          </svg>
          <div className="crt-chart-labels"><span>JAN</span><span>MAR</span><span>MAY</span><span>JUL</span><span>SEP</span><span>NOV</span></div>
        </div>
        <div className="crt-preview-foot">
          <div><span className="crt-foot-dot" /><span>Project-wise view</span></div>
          <span>Trends · Targets · Forecasts</span>
        </div>
      </div>
      <div className="crt-float-note">
        <span className="crt-float-icon"><TrendingUp size={16} /></span>
        <span><strong>Clearer decisions</strong><small>One shared view of performance</small></span>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <main className="crt-landing">
      <header className="crt-header">
        <a
          href="https://globalhealthcareresource.com"
          target="_blank"
          rel="noopener noreferrer"
          className="crt-brand"
          aria-label="Visit Global Healthcare Resource website (opens in a new tab)"
        >
          <img src="/global-logo-transparent.png" alt="Global Healthcare Resource" />
          <span className="crt-brand-divider" />
          <span className="crt-brand-product">Client revenue<br />workspace</span>
        </a>
        <Link to="/login" className="crt-signin">
          Sign in <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </header>

      <section className="crt-hero">
        <div className="crt-hero-copy">
          <div className="crt-eyebrow"><span /> REVENUE VISIBILITY, IN ONE PLACE</div>
          <h1><strong>Global CRT (Client Revenue Tracker)</strong></h1>
          <p className="crt-intro">
            Global CRT (Client Revenue Tracker) is a new revenue tracking application designed to provide real-time visibility into revenue performance across all Client projects.
          </p>
          <p className="crt-description">
            The application gives Operations, Finance, and Leadership a centralized view of key revenue metrics, enabling faster decision-making and improved financial oversight. It will help monitor project-wise revenue, identify trends, compare actuals against targets, and provide actionable insights through intuitive dashboards and reports.
          </p>
          <div className="crt-hero-actions">
            <Link to="/login" className="crt-primary-action">
              Access Workspace <ArrowRight size={17} aria-hidden="true" />
            </Link>
            <span className="crt-access-note"><LockKeyhole size={14} /> For authorized team members</span>
          </div>
        </div>
        <div className="crt-hero-visual">
          <div className="crt-visual-orbit crt-orbit-one" />
          <div className="crt-visual-orbit crt-orbit-two" />
          <RevenuePreview />
          <div className="crt-visual-caption"><span>CLIENT REVENUE TRACKER</span><span>GLOBAL HEALTHCARE RESOURCE</span></div>
        </div>
      </section>

      <section className="crt-benefits" aria-labelledby="crt-benefits-heading">
        <div className="crt-benefits-heading">
          <div>
            <div className="crt-eyebrow"><span /> BUILT FOR BETTER OVERSIGHT</div>
            <h2 id="crt-benefits-heading">See the whole picture.<br /><em>Move with confidence.</em></h2>
          </div>
          <p>Some of the key benefits include:</p>
        </div>
        <ul className="crt-benefit-list">
          {benefits.map(({ icon: Icon, title }, index) => (
            <li key={title}>
              <span className="crt-benefit-index">0{index + 1}</span>
              <span className="crt-benefit-icon"><Icon size={19} strokeWidth={1.8} /></span>
              <span className="crt-benefit-title">{title}</span>
              <ArrowRight className="crt-benefit-arrow" size={16} aria-hidden="true" />
            </li>
          ))}
        </ul>
      </section>

      <footer className="crt-footer">
        <a
          href="https://globalhealthcareresource.com"
          target="_blank"
          rel="noopener noreferrer"
          className="crt-footer-brand"
          aria-label="Visit Global Healthcare Resource website (opens in a new tab)"
        >
          <img src="/global-logo-transparent.png" alt="Global Healthcare Resource" />
        </a>
        <span>Clear insight for every client project.</span>
        <Link to="/login">Access your workspace <ArrowRight size={14} /></Link>
      </footer>
    </main>
  );
}
